import axios, { AxiosInstance } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useMemo } from 'react';
import { safeGet } from '../../utils/objectUtils';
import { readCookie } from '../../utils/cookieUtils';
import { useConfig } from '../../providers/config-provider';
import useAxiosLoader from './useAxiosLoader';
import { isLocalEnv } from '../functions/isLocalEnv';
import { createClient } from 'graphql-sse';
import { buildUrl } from '../../utils/common';

const useServiceClients = (isLoaderEnabled = true) => {
  const { config } = useConfig();

  const createSseClient = (configPath: string) => {
    const visionSseConfig = {
      baseUrl: safeGet(config, `${configPath}.url`, ''),
      envlbl: safeGet(config, `${configPath}.envlbl`, ''),
    };

    if (visionSseConfig.baseUrl?.length) {
      const targetURL = buildUrl(
        visionSseConfig.baseUrl,
        '/api/loginByGuid-sse',
        visionSseConfig.envlbl,
      );

      return createClient({
        url: targetURL,
        credentials: 'include',

        headers: () => {
          const headers: Record<string, string> = {
            'content-type': 'application/json',
            'x-correlation-id': uuidv4(),
          };

          if (isLocalEnv()) {
            const tempObsso = readCookie('temp-obsso');
            if (tempObsso) {
              try {
                const parsed = JSON.parse(tempObsso);
                if (parsed['x-guid']) {
                  headers['x-guid'] = parsed['x-guid'];
                }
              } catch {}
            }
          }

          return headers;
        },
      });
    }
  };

  const visionWebClient = useMemo(
    () =>
      axios.create({
        baseURL: safeGet(config, 'services.kpvisionweb.url', ''),
        timeout: safeGet(config, 'services.kpvisionweb.timeout', 15000),
        headers: {
          'Content-Type': 'application/json',
          'x-correlation-id': process.env.NODE_ENV === 'test' ? '' : uuidv4(),
        },
        withCredentials: true,
      }),
    [config],
  );

  const createBffClient = (configPath: string) => {
    const visionBffConfig = {
      baseUrl: safeGet(config, `${configPath}.url`, ''),
      envlbl: safeGet(config, `${configPath}.envlbl`, ''),
      timeout: safeGet(config, `${configPath}.timeout`, 15000),
    };

    const bffInstance = axios.create({
      baseURL: visionBffConfig.baseUrl,
      timeout: visionBffConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    bffInstance.interceptors?.request.use(
      function (config) {
        (config?.headers as any)['x-correlation-id'] = uuidv4();
        if (visionBffConfig.envlbl?.length) {
          const url = new URL(config.url ?? '', config.baseURL);
          url.searchParams.append('envlbl', visionBffConfig.envlbl);
          config.url = url.toString().replace(url.origin, '');
        }
        // Only for local testing
        if (isLocalEnv()) {
          if (readCookie('temp-obsso')) {
            (config?.headers as any)['x-guid'] = JSON.parse(readCookie('temp-obsso'))['x-guid'];
          }
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      },
    );

    return bffInstance;
  };

  const visionSseClient = useMemo(() => createSseClient('services.kpvisionbff'), [config]);
  const visionBffClient = useMemo(() => createBffClient('services.kpvisionbff'), [config]);
  const visionBffMocksClient = useMemo(
    () => createBffClient('services.kpvisionbffmocks'),
    [config],
  );
  const ctproxyBffClient: AxiosInstance = useMemo(
    () => createBffClient('services.kpctproxybff'),
    [config],
  );
  // const ctproxyBffClient = useMemo(
  //   () =>
  //     axios.create({
  //       baseURL: safeGet(config, 'services.kpctproxybff.url', ''),
  //       timeout: safeGet(config, 'services.kpctproxybff.timeout', 15000),
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'x-correlation-id': process.env.NODE_ENV === 'test'?'':uuidv4(),
  //       },
  //       withCredentials: true,
  //     }),
  //   [config],
  // );

  // Setup loading dots
  useAxiosLoader(visionWebClient, isLoaderEnabled);
  useAxiosLoader(visionBffClient, isLoaderEnabled);
  useAxiosLoader(visionBffMocksClient, isLoaderEnabled);
  useAxiosLoader(ctproxyBffClient, isLoaderEnabled);

  return {
    visionSseClient,
    visionWebClient,
    visionBffClient,
    visionBffMocksClient,
    ctproxyBffClient,
  };
};

export default useServiceClients;
